import React, { useState } from 'react'
import {
  Box,
  Container,
  VStack,
  Text,
  Card,
  CardHeader,
  CardBody,
  Heading,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  Link,
  Image,
} from '@chakra-ui/react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { FiArrowLeft } from 'react-icons/fi'
import apiService from '@/services/api'
import CoreTechLogo from '@/assets/logo.svg'

export default function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState('')

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      setError('Email address is required')
      return
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      await apiService.forgotPassword(email.trim())
      setIsSubmitted(true)
    } catch (error: any) {
      console.error('Forgot password request failed:', error)
      // Always show success for security reasons
      setIsSubmitted(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (value: string) => {
    setEmail(value)
    if (error) {
      setError('')
    }
  }

  if (isSubmitted) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <Container maxW="md" py={8}>
          <VStack spacing={8}>
            <Image src={CoreTechLogo} alt="CoreTech" h="12" />

            <Card w="full" boxShadow="xl" borderRadius="xl">
              <CardHeader textAlign="center" pb={4}>
                <Heading size="lg" color="gray.800">
                  Check Your Email
                </Heading>
              </CardHeader>

              <CardBody pt={0}>
                <VStack spacing={6}>
                  <Alert status="success" borderRadius="lg">
                    <AlertIcon />
                    <AlertDescription>
                      If an account with that email exists, we've sent you a password reset link.
                      Please check your email and follow the instructions to reset your password.
                    </AlertDescription>
                  </Alert>

                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    Didn't receive an email? Check your spam folder or try again in a few minutes.
                  </Text>

                  <VStack spacing={3} w="full">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsSubmitted(false)
                        setEmail('')
                      }}
                      w="full"
                    >
                      Try Different Email
                    </Button>

                    <Link
                      as={RouterLink}
                      to="/login"
                      fontSize="sm"
                      color="brand.500"
                      _hover={{ color: 'brand.600' }}
                    >
                      ← Back to Sign In
                    </Link>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </Container>
      </Box>
    )
  }

  return (
    <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
      <Container maxW="md" py={8}>
        <VStack spacing={8}>
          <Image src={CoreTechLogo} alt="CoreTech" h="12" />

          <Card w="full" boxShadow="xl" borderRadius="xl">
            <CardHeader textAlign="center" pb={4}>
              <Heading size="lg" color="gray.800">
                Forgot Your Password?
              </Heading>
              <Text mt={2} fontSize="sm" color="gray.600">
                Enter your email address and we'll send you a link to reset your password.
              </Text>
            </CardHeader>

            <CardBody pt={0}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={6}>
                  {error && (
                    <Alert status="error" borderRadius="lg">
                      <AlertIcon />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <FormControl isInvalid={!!error}>
                    <FormLabel>Email Address</FormLabel>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => handleInputChange(e.target.value)}
                      placeholder="Enter your email address"
                      size="lg"
                      borderRadius="lg"
                      bg="white"
                      _focus={{
                        borderColor: 'brand.500',
                        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                      }}
                    />
                    <FormErrorMessage>{error}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="brand"
                    size="lg"
                    w="full"
                    borderRadius="lg"
                    isLoading={isLoading}
                    loadingText="Sending Reset Link..."
                  >
                    Send Reset Link
                  </Button>

                  <Link
                    as={RouterLink}
                    to="/login"
                    fontSize="sm"
                    color="gray.600"
                    _hover={{ color: 'brand.500' }}
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    <FiArrowLeft size="16" />
                    Back to Sign In
                  </Link>
                </VStack>
              </form>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  )
}
